<template>
  <div class="designer-container">
    <div class="no-path" v-if="!currentPath">No path selected</div>
    <div class="path-heading sub-heading sub-heading-right" v-if="currentPath">
      <h5 class="title is-6">
        <EditableText :value="currentPath.name" @text-updated="pathNameUpdated" minWidth="200px" maxWidth="800px">
        </EditableText>
      </h5>

      <div class="delete-path-button" v-if="!isFirstPathInFlow">
        <a @click="openDeletePathModal()">
          <DeleteIcon class="icon-1-3x"></DeleteIcon>
        </a>
      </div>
    </div>

    <div class="path-contents" v-if="currentPath">
      <TriggerBox
        v-if="isFirstPathInFlow"
        :value="trigger"
        :triggerConditions="triggerConditions"
        :audienceConditions="audienceConditions"
        :schedule="scheduleCondition"
        :openingTimes="openingTimes"
      />

      <LinkedPathsBox></LinkedPathsBox>

      <Draggable v-model="reorderableStates" class="state-list" handle=".island-badge" item-key="id">
        <template #item="{ element: state }">
          <div class="state" :key="state.id">
            <component
              :ref="(el: any) => setStateRef(el, state.id)"
              :is="getStateComponent(state)"
              :value="state"
              @state-updated="stateUpdated"
            />
            <div class="state-menu">
              <a id="delete-state" class="delete is-small delete-state" @click="openDeleteStateModal(state)"></a>
            </div>
          </div>
        </template>
      </Draggable>

      <DropDownMenu
        class="add-state-container"
        dropDownIcon="big-add-circle"
        @isUp="setIsStatePickerUp"
        :class="{ 'is-up': isStatePickerUp }"
      >
        <a @click="addMessageState()" class="dropdown-item">
          <TextIcon></TextIcon>
          Message
        </a>

        <a @click="addQuestionState()" class="dropdown-item">
          <QuestionIcon />
          Question
        </a>

        <a @click="addDecisionState()" class="dropdown-item">
          <DecisionIcon />
          Decision
        </a>

        <a @click="addQuickReplyState()" class="dropdown-item">
          <QuickReplyIcon />
          Quick Replies
        </a>

        <a @click="addAppIntegrationState()" class="dropdown-item">
          <span class="cube-icon">
            <font-awesome-icon icon="cube"></font-awesome-icon>
          </span>
          App Action
        </a>

        <a @click="addGoToState()" class="dropdown-item" v-if="!hasEndingStates">
          <GoToIcon />
          Go To
        </a>

        <a @click="addHumanHandoverState()" class="dropdown-item" v-if="!hasEndingStates">
          <PauseBotIcon />
          Pause Bot
        </a>
      </DropDownMenu>

      <div class="state-list ending-states" v-if="hasEndingStates">
        <div class="state" v-for="state in endStates" :key="state.id">
          <component :is="getStateComponent(state)" :value="state" @state-updated="stateUpdated"></component>

          <div class="state-menu">
            <a class="delete is-small" @click="openDeleteStateModal(state)"></a>
          </div>
        </div>
      </div>
    </div>

    <Modal
      id="delete-path-modal"
      v-if="deleteModalOptions.open && deleteModalOptions.type === DeleteModalType.Path"
      v-on:cancel="deleteModalOptions.cancel()"
    >
      <template v-slot:title>
        <p class="modal-card-title">{{ deleteModalOptions.title }}</p>
      </template>

      <template v-slot:body class="modal-card-body">
        <p v-if="linkedPaths.length > 0">This path is linked to by the following path(s):</p>

        <ul>
          <div v-for="linkedPath in linkedPaths" :key="linkedPath.id">
            <li>{{ linkedPath.name }}</li>
          </div>
        </ul>

        <p id="linked-path-message" v-if="linkedPaths.length > 0">
          These paths will become invalid if you delete {{ pathName }}. Are you sure you want to continue?
        </p>
        <p v-if="linkedPaths.length === 0">Are you sure you want to delete {{ pathName }}?</p>
      </template>

      <template v-slot:buttons>
        <n-button-group>
          <n-button variant="primary" @click="deleteModalOptions.confirm()">Delete</n-button>
          <n-button @click="deleteModalOptions.cancel()" variant="primary--outline">Cancel</n-button>
        </n-button-group>
      </template>
    </Modal>

    <ConfirmationModal
      id="delete-modal"
      v-if="deleteModalOptions.open && deleteModalOptions.type === DeleteModalType.State"
      :confirmationTitle="deleteModalOptions.title"
      :confirmationMessage="deleteModalOptions.confirmation"
      @confirm="deleteModalOptions.confirm()"
      @cancel="deleteModalOptions.cancel()"
    ></ConfirmationModal>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import Draggable from 'vuedraggable';
import { nanoid } from 'nanoid';
import { StateViewModel, PathViewModel } from '@/features/bots/models/designer';
import EditableText from '@/shared/components/EditableText.vue';
import ConfirmationModal from '@/shared/components/ConfirmationModal.vue';
import Modal from '@/shared/components/Modal.vue';
import TextComponent from './TextComponent.vue';
import InputStringComponent from './InputStringComponent.vue';
import QuickReplyComponent from './QuickReplyComponent.vue';
import HumanHandoverComponent from './HumanHandoverComponent.vue';
import AppIntegrationComponent from './AppIntegrationComponent.vue';
import DecisionComponent from './DecisionComponent.vue';
import GoToComponent from './GoToComponent.vue';
import DropDownMenu from '@/shared/components/dropDownMenu/DropDownMenu.vue';
import TriggerBox from './TriggerBox.vue';
import LinkedPathsBox from './LinkedPathsBox.vue';
import QuestionIcon from 'vue-material-design-icons/ForumOutline.vue';
import QuickReplyIcon from 'vue-material-design-icons/PlaylistCheck.vue';
import DecisionIcon from 'vue-material-design-icons/SourceBranch.vue';
import PauseBotIcon from 'vue-material-design-icons/Pause.vue';
import TextIcon from 'vue-material-design-icons/FormatTitle.vue';
import DeleteIcon from 'vue-material-design-icons/TrashCanOutline.vue';
import GoToIcon from '@/shared/icons/GoToIcon.vue';
import { DELETE_PATH } from '@/store/bot-designer/paths/actionTypes';
import { ADD_STATE, UPDATE_STATE } from '@/store/bot-designer/states/mutationTypes';
import { DELETE_STATE } from '@/store/bot-designer/states/actionTypes';
import { UPDATE_PATH } from '@/store/bot-designer/paths/mutationTypes';
import { ConditionViewModel } from '@shared/components/conditions/models/conditionViewModel';
import { usePath } from '@/features/bots/models/designer/usePath';

const COMPONENTS = {
  TextComponent,
  InputStringComponent,
  QuickReplyComponent,
  HumanHandoverComponent,
  AppIntegrationComponent,
  DecisionComponent,
  GoToComponent,
};

enum DeleteModalType {
  State,
  Path,
}

const store = useStore();
const currentPathId = computed<string>(() => store.getters.currentPathId);
const currentPath = computed<PathViewModel>(() => store.getters.currentPath);
const firstPath = computed(() => store.getters.firstPath);
const linkedPaths = computed(() => store.getters.linkedPaths);
const trigger = computed(() => store.getters.trigger);
const triggerConditions = computed(() => store.getters.triggerConditions);
const audienceConditions = computed(() => store.getters.audienceConditions);
const scheduleCondition = computed<ConditionViewModel | undefined>(() => store.getters.scheduleCondition);
const openingTimes = computed(() => store.getters.openingTimes);

const { reorderableStates, pathName, hasEndingStates, endStates } = usePath(currentPath);

const isStatePickerUp = ref(false);

const deleteModalOptions = ref({
  open: false,
  type: DeleteModalType.State,
  title: '',
  confirmation: '',
  confirm: () => {},
  cancel: () => {},
});

const stateRefs = ref<{ [key: string]: any }>({});

function setStateRef(el: any, id: string) {
  if (el) {
    stateRefs.value[id] = el;
  }
}

const openDeletePathModal = () => {
  deleteModalOptions.value = {
    open: true,
    type: DeleteModalType.Path,
    title: 'Delete Path',
    confirmation: '',
    confirm: deletePathConfirmed,
    cancel: closeDeleteModal,
  };
};

const openDeleteStateModal = (state: StateViewModel) => {
  deleteModalOptions.value = {
    open: true,
    type: DeleteModalType.State,
    title: 'Delete State',
    confirmation: 'Are you sure you want to delete the state?',
    confirm: () => deleteStateConfirmed(state),
    cancel: closeDeleteModal,
  };
};

const closeDeleteModal = () => {
  deleteModalOptions.value.open = false;
};

const deletePathConfirmed = () => {
  store.dispatch(DELETE_PATH, currentPath.value.id);
  closeDeleteModal();
};

const deleteStateConfirmed = (state: StateViewModel) => {
  store.dispatch(DELETE_STATE, state);
  closeDeleteModal();
};

const getStateComponent = (state: StateViewModel) => {
  if (state.component === 'InputString' && state.options?.length) {
    return COMPONENTS.QuickReplyComponent;
  }
  const name = `${state.component}Component`;
  return (COMPONENTS as any)[name] || name;
};

const isFirstPathInFlow = computed(() => currentPath.value?.id === firstPath.value?.id);

const addState = (component: string, additionalProps = {}) => {
  store.commit(ADD_STATE, {
    id: `state-${nanoid(7)}`,
    component,
    ...additionalProps,
  });
  focusOnStateInput();
};

const addMessageState = () => addState('Text', { text: '', invalid: true });
const addQuestionState = () => addState('InputString', { text: '', invalid: true });
const addDecisionState = () =>
  addState('Decision', {
    invalid: true,
    decisions: [{ transition: {}, conditions: { type: 'and', conditions: [] } }],
    noMatch: {},
  });
const addAppIntegrationState = () => addState('AppIntegration');
const addQuickReplyState = () =>
  addState('InputString', { text: '', allowedAttempts: 1, options: [{ id: '1', text: '' }], invalid: true });
const addHumanHandoverState = () => addState('HumanHandover');
const addGoToState = () => addState('GoTo', { transition: {}, invalid: true });

const stateUpdated = (state: StateViewModel) => {
  store.commit(UPDATE_STATE, state);
};

const pathNameUpdated = (text: string) => {
  currentPath.value.name = text;
  store.commit(UPDATE_PATH, currentPath.value);
};

const setIsStatePickerUp = (val: boolean) => {
  isStatePickerUp.value = val;
};

const focusOnStateInput = async () => {
  await nextTick();
  const lastState = getLastAddedState();
  if (lastState && typeof lastState.focusOnInput === 'function') {
    lastState.focusOnInput();
  }
};

const getLastAddedState = () => {
  const states = currentPath.value.states;
  if (!states.length) return null;
  const lastStateId = states[states.length - 1].id;
  return stateRefs.value[lastStateId] || null;
};

watch(
  currentPath,
  (newValue: PathViewModel, oldValue: PathViewModel) => {
    if (oldValue && newValue.id !== oldValue.id) {
      store.commit(UPDATE_PATH, newValue);
    }
  },
  { deep: true, immediate: true },
);
</script>

<style scoped lang="scss">
#linked-path-message {
  margin-top: 10px;
}
li::before {
  content: '•';
  color: $color-netcall-salmon;
}
.path-heading {
  flex-shrink: 0;
  display: flex;
  align-items: center;

  .title {
    margin-bottom: 0;
    padding-right: 1.5rem;
  }
}

.path-contents {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 45%;
  padding: 1em;
}

.state-list {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.designer-container {
  display: flex;
  flex-direction: column;
  flex: 5;
  background-color: #dbdfe8;
  min-width: 900px;
}

.state {
  margin-top: 1rem;

  display: flex;
  position: relative;
  min-height: 60px;
  background: white;
  border-radius: 5px;
  box-shadow: 0 3px 8px 0 rgba(0, 0, 0, 0.1);

  &:focus-within {
    z-index: 2;
  }

  .state-menu {
    position: absolute;
    top: 5px;
    right: 5px;
    color: rgba(10, 10, 10, 0.2);

    &:hover {
      color: rgba(10, 10, 10, 0.4);
    }
  }
}

.add-state-container {
  margin-top: 2.5rem;
  margin-bottom: 1.5rem;
  align-self: center;
  display: inline;

  span {
    padding-right: 30px;
  }

  .cube-icon {
    font-size: 13px;
    padding-right: 17px;
  }
}
a {
  display: inline-block;
}

.no-path {
  font-size: 2em;
}

:deep(.invalid-warning) {
  position: absolute;
  right: -2.5em;
  top: 50%;
  transform: translate(-50%, -50%);
  color: red;

  &.shift-for-expander {
    right: -5em;
  }
}

.delete-path-button a {
  color: #363636;

  &:hover {
    color: #5e5e5e;
  }
}

.ending-states {
  padding-bottom: 300px;
}

.is-up {
  .dropdown-menu {
    margin-bottom: 5px;
  }
}
</style>
